import { Box, Textarea } from "@chakra-ui/react";
import SharedSelect from "../../../../../../shared/components/Select";
import {
  EntityProfileSegmentedControl,
  entityProfileListToText,
  entityProfileTextToList,
} from "../../../../../entityProfileModal/components/EntityProfilePreferenceControls";
import type { StaffingPreferencesBody } from "../staffingPreferences.types";
import {
  getLanguageSelectValue,
  kidsCountOptions,
  languageOptions,
} from "../staffingPreferences.utils";

export function StaffingBooleanValue(props: {
  value: boolean | null;
  onChange?: (value: boolean | null) => void;
}) {
  return (
    <EntityProfileSegmentedControl
      options={[
        { label: "Yes", value: true },
        { label: "No", value: false },
        { label: "Unknown", value: null },
      ]}
      value={props.value}
      onChange={
        props.onChange === undefined
          ? undefined
          : (value) => props.onChange?.(typeof value === "boolean" ? value : null)
      }
    />
  );
}

export function StaffingGenderValue(props: {
  value: string | null;
  onChange?: (value: string | null) => void;
}) {
  return (
    <EntityProfileSegmentedControl
      options={[
        { label: "Male", value: "Male" },
        { label: "Female", value: "Female" },
        { label: "Both", value: "Both" },
        { label: "Unknown", value: null },
      ]}
      value={props.value}
      onChange={
        props.onChange === undefined
          ? undefined
          : (value) => props.onChange?.(typeof value === "string" ? value : null)
      }
    />
  );
}

export function StaffingPreferenceEditor(props: {
  draft: StaffingPreferencesBody;
  fieldKey: keyof StaffingPreferencesBody;
  onChangeDraft: (draft: StaffingPreferencesBody) => void;
  onCommitDraft: (draft: StaffingPreferencesBody) => void;
}) {
  const { draft, fieldKey } = props;
  const commitDraftField = <TKey extends keyof StaffingPreferencesBody>(
    key: TKey,
    value: StaffingPreferencesBody[TKey]
  ) => {
    const nextDraft = { ...draft, [key]: value };

    props.onChangeDraft(nextDraft);
    props.onCommitDraft(nextDraft);
  };

  if (fieldKey === "gender") {
    return (
      <StaffingGenderValue
        value={draft.gender}
        onChange={(value) => commitDraftField("gender", value)}
      />
    );
  }

  if (fieldKey === "mainLanguage" || fieldKey === "secondaryLanguage") {
    return (
      <Box minW="180px">
        <SharedSelect
          label="Select language"
          multiple={false}
          options={languageOptions}
          value={getLanguageSelectValue(draft[fieldKey])}
          onChange={(value) => commitDraftField(fieldKey, value ?? null)}
        />
      </Box>
    );
  }

  if (fieldKey === "kidsCount") {
    return (
      <Box minW="150px">
        <SharedSelect
          label="Select count"
          multiple={false}
          options={kidsCountOptions}
          value={draft.kidsCount}
          onChange={(value) => commitDraftField("kidsCount", value ?? null)}
        />
      </Box>
    );
  }

  if (fieldKey === "petsDetails") {
    return (
      <Textarea
        minW="220px"
        rows={2}
        size="sm"
        value={entityProfileListToText(draft.petsDetails)}
        onBlur={(event) => {
          props.onCommitDraft({
            ...draft,
            petsDetails: entityProfileTextToList(event.currentTarget.value),
          });
        }}
        onChange={(event) =>
          props.onChangeDraft({
            ...draft,
            petsDetails: entityProfileTextToList(event.target.value),
          })
        }
      />
    );
  }

  return null;
}
